import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { Select } from './common/Catalyst/select';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import { bankService } from './clients/ApiClients';
import {
  CounterpartyBankAccountReturn,
  PayableReturn,
} from './clients/rust-api-client';
import { getBankErrorParts } from './utils/bankHelpers';
import { useUser } from './hooks/UserContext';
import { canManagePayees, canPayVendor } from './constants/UserTypes';
import VendorPayDialog from './VendorPayDialog';
import VendorPaymentsList from './VendorPaymentsList';

interface VendorBankDetailsCardProps {
  hoaId: string;
  vendorId: string;
  /** Defaults the printed name, cut to the bank's 22-character cap. */
  vendorName: string;
}

/** The bank prints at most this many characters of the payee name. */
const ACH_NAME_MAX = 22;

const defaultAchName = (vendorName: string): string => {
  const trimmed = vendorName.trim();
  if (trimmed.length <= ACH_NAME_MAX) return trimmed;
  const cut = trimmed.slice(0, ACH_NAME_MAX);
  const atWord = cut.lastIndexOf(' ');
  return (atWord > 8 ? cut.slice(0, atWord) : cut).trim();
};

/**
 * A vendor's bank account for ACH payments. The account number is
 * write-only: entered here, encrypted by the API, and never shown again
 * beyond its last four. Replacing the details retires the old row rather
 * than editing it, so payments already made keep pointing at what they
 * were sent to.
 */
const VendorBankDetailsCard: React.FC<VendorBankDetailsCardProps> = ({
  hoaId,
  vendorId,
  vendorName,
}) => {
  const { userType } = useUser();
  const mayManage = canManagePayees(userType);
  const mayPay = canPayVendor(userType);
  const [paying, setPaying] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState<PayableReturn | null>(
    null
  );

  const [details, setDetails] = useState<CounterpartyBankAccountReturn | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [achName, setAchName] = useState('');
  const [entityType, setEntityType] = useState('corporate');
  const [accountType, setAccountType] = useState('checking');
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [accountAgain, setAccountAgain] = useState('');
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await bankService.getVendorBankAccount({ hoaId, vendorId });
      setDetails(resp.data);
    } catch (e) {
      const { status, body } = getBankErrorParts(e);
      if (status === 404) {
        setDetails(null);
      } else {
        setError(body || "Couldn't load the vendor's bank details.");
      }
    } finally {
      setLoading(false);
    }
  }, [hoaId, vendorId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEditor = () => {
    setAchName(details?.ach_name ?? defaultAchName(vendorName));
    setEntityType(details?.entity_type ?? 'corporate');
    setAccountType(details?.account_type ?? 'checking');
    setRouting(details?.routing_number ?? '');
    setAccount('');
    setAccountAgain('');
    setLabel(details?.label ?? '');
    setFormError(null);
    setEditing(true);
  };

  const save = async () => {
    if (achName.trim().length === 0 || achName.trim().length > ACH_NAME_MAX) {
      setFormError(
        `The name the bank prints must be 1 to ${ACH_NAME_MAX} characters.`
      );
      return;
    }
    if (!/^\d{9}$/.test(routing.replace(/\s/g, ''))) {
      setFormError('The routing number is nine digits.');
      return;
    }
    if (account.replace(/\s/g, '') !== accountAgain.replace(/\s/g, '')) {
      setFormError('The account numbers do not match.');
      return;
    }
    try {
      setSubmitting(true);
      setFormError(null);
      const resp = await bankService.setVendorBankAccount({
        hoaId,
        vendorId,
        counterpartyBankAccountUpsert: {
          ach_name: achName.trim(),
          entity_type: entityType,
          account_type: accountType,
          routing_number: routing.replace(/\s/g, ''),
          account_number: account.replace(/\s/g, ''),
          label: label.trim() || null,
        },
      });
      setDetails(resp.data);
      setEditing(false);
      setAccount('');
      setAccountAgain('');
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setFormError(body || "Couldn't save the bank details.");
    } finally {
      setSubmitting(false);
    }
  };

  const retire = async () => {
    try {
      setSubmitting(true);
      await bankService.retireVendorBankAccount({ hoaId, vendorId });
      setDetails(null);
      setConfirmRetire(false);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setError(body || "Couldn't remove the bank details.");
      setConfirmRetire(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Bank details for ACH payments
        </h3>
        {(mayManage || mayPay) && !loading && (
          <div className="flex shrink-0 gap-2 whitespace-nowrap">
            {details && mayPay && (
              <Button color="sky" onClick={() => setPaying(true)}>
                Pay by ACH
              </Button>
            )}
            {mayManage &&
              (details ? (
                <Button outline onClick={openEditor}>
                  Replace
                </Button>
              ) : (
                <Button color="sky" onClick={openEditor}>
                  Add
                </Button>
              ))}
            {details && mayManage && (
              <Button outline onClick={() => setConfirmRetire(true)}>
                Remove
              </Button>
            )}
          </div>
        )}
      </div>

      {error && (
        <ShowAlert
          alertType="error"
          alertTitle="Bank details"
          alertMessage={error}
          isVisible={!!error}
          onClose={() => setError(null)}
          timeout={10000}
          isInline={true}
        />
      )}

      {loading ? (
        <Text className="text-sm">Loading…</Text>
      ) : details ? (
        <dl className="space-y-3">
          <div className="flex flex-col">
            <dt className="text-sm font-semibold text-gray-900 dark:text-white">
              Name the bank prints
            </dt>
            <dd className="text-sm">{details.ach_name}</dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-sm font-semibold text-gray-900 dark:text-white">
              Account
            </dt>
            <dd className="text-sm tabular-nums">
              {details.account_type} ending in {details.account_last4}
              <Badge color="zinc" className="ml-2">
                {details.entity_type}
              </Badge>
            </dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-sm font-semibold text-gray-900 dark:text-white">
              Routing
            </dt>
            <dd className="text-sm tabular-nums">{details.routing_number}</dd>
          </div>
          {details.label && (
            <div className="flex flex-col">
              <dt className="text-sm font-semibold text-gray-900 dark:text-white">
                Label
              </dt>
              <dd className="text-sm">{details.label}</dd>
            </div>
          )}
          <Text className="text-xs">
            The full account number is encrypted and cannot be shown again. To
            change it, replace the details.
          </Text>
        </dl>
      ) : (
        <Text className="text-sm">
          No bank account on file.{' '}
          {mayManage
            ? 'Add one to pay this vendor by ACH.'
            : 'An Owner or Admin can add one.'}
        </Text>
      )}

      {/* The vendor's payments, whether or not an account is on file now:
          a retired account's payments are still history. */}
      <VendorPaymentsList
        hoaId={hoaId}
        vendorId={vendorId}
        justSubmitted={justSubmitted}
      />

      <VendorPayDialog
        open={paying}
        onClose={() => setPaying(false)}
        hoaId={hoaId}
        vendorId={vendorId}
        vendorName={vendorName}
        onSubmitted={setJustSubmitted}
      />

      <Dialog
        open={editing}
        onClose={submitting ? () => undefined : () => setEditing(false)}
      >
        <DialogTitle>
          {details ? 'Replace bank details' : 'Add bank details'}
        </DialogTitle>
        <DialogDescription>
          {vendorName}. The account number is encrypted when saved and never
          shown again.
        </DialogDescription>
        <DialogBody>
          {formError && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle="Cannot save"
                alertMessage={formError}
                isVisible={!!formError}
                onClose={() => setFormError(null)}
                timeout={12000}
                isInline={true}
              />
            </div>
          )}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name the bank prints</Label>
                <Input
                  name="ach_name"
                  value={achName}
                  maxLength={ACH_NAME_MAX}
                  onChange={(e) => setAchName(e.target.value)}
                />
                <Description>
                  At most {ACH_NAME_MAX} characters; this is what appears on the
                  vendor's statement. {achName.trim().length}/{ACH_NAME_MAX}
                </Description>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Entity</Label>
                  <Select
                    name="entity_type"
                    value={entityType}
                    onChange={(e) => setEntityType(e.target.value)}
                  >
                    <option value="corporate">Business</option>
                    <option value="personal">Individual</option>
                  </Select>
                </Field>
                <Field>
                  <Label>Account type</Label>
                  <Select
                    name="account_type"
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </Select>
                </Field>
              </div>
              <Field>
                <Label>Routing number</Label>
                <Input
                  name="routing_number"
                  inputMode="numeric"
                  autoComplete="off"
                  value={routing}
                  onChange={(e) => setRouting(e.target.value)}
                />
                <Description>
                  Nine digits, from the vendor's check or their bank.
                </Description>
              </Field>
              <Field>
                <Label>Account number</Label>
                <Input
                  name="account_number"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Account number again</Label>
                <Input
                  name="account_number_again"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  value={accountAgain}
                  onChange={(e) => setAccountAgain(e.target.value)}
                />
                <Description>
                  Typed twice because it cannot be read back.
                </Description>
              </Field>
              <Field>
                <Label>Label (optional)</Label>
                <Input
                  name="label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Operating"
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setEditing(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button color="sky" onClick={save} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRetire} onClose={() => setConfirmRetire(false)}>
        <DialogTitle>Remove bank details?</DialogTitle>
        <DialogDescription>
          New ACH payments to {vendorName} will not be possible until details
          are added again. Payments already made keep their record.
        </DialogDescription>
        <DialogActions>
          <Button
            plain
            onClick={() => setConfirmRetire(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button color="red" onClick={retire} disabled={submitting}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VendorBankDetailsCard;
